import { useMemo, useState } from 'react';
import moment from 'moment';
import { chip, chipActive, chipIdle } from '@/components/ui/design';
import { computeDashboard, seriesEntries, type DashboardMetric } from '@/utils/dashboardMetrics';

type Tier = { name: string; color?: string; seatCount: number };
type Props = { orders: any[]; capacity: number; tiers: Tier[]; cur: string; endDate?: string | null };

const PERIODS = [
    { key: 'hour', label: 'Hour', unit: 'hour', format: 'h A' },
    { key: 'day', label: 'Day', unit: 'day', format: 'MMM D' },
    { key: 'week', label: 'Week', unit: 'isoWeek', format: 'MMM D' },
    { key: 'month', label: 'Month', unit: 'month', format: 'MMM' },
] as const;
const METRICS: { key: DashboardMetric; label: string; unit: string }[] = [
    { key: 'sales', label: 'Tickets Sold', unit: 'sold' },
    { key: 'revenue', label: 'Net Revenue', unit: 'net' },
    { key: 'checkins', label: 'Check-ins', unit: 'checked in' },
];
const BUCKETS = 6;
const HOURS_IN_DAY = 24;
const W = 640;
const H = 180;
const PAD = { left: 48, right: 16, top: 16, bottom: 32 };
const TIP_W = 124;
const TIP_H = 42;

const tile = 'rounded-[28px] bg-grey99 p-5 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
const tileLabel = 'text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted';
const tileValue = 'text-[clamp(24px,2.6vw,34px)] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const tileNote = 'mt-2 text-xs text-grey40 dark:text-nightMuted';
const panel = 'rounded-[28px] bg-white p-5 ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 lg:p-6';
const heading = 'mb-4 text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white';

const smoothPath = (points: { x: number; y: number }[]) => points.reduce((path, point, index) => {
    if (index === 0) return `M${point.x} ${point.y}`;
    const prev = points[index - 1];
    const mid = (prev.x + point.x) / 2;
    return `${path} C${mid} ${prev.y} ${mid} ${point.y} ${point.x} ${point.y}`;
}, '');

export default function EventSalesDashboard({ orders, capacity, tiers, cur, endDate }: Props) {
    const [metric, setMetric] = useState<DashboardMetric>('sales');
    const [period, setPeriod] = useState<(typeof PERIODS)[number]['key']>('day');
    const [picked, setPicked] = useState<number | null>(null);
    const [hovered, setHovered] = useState<number | null>(null);

    const money = (value: number) => `${cur}${Math.round(value).toLocaleString('en-US')}`;
    const money2 = (value: number) => `${cur}${value.toFixed(2)}`;
    const data = useMemo(() => computeDashboard(orders, capacity, tiers), [orders, capacity, tiers]);

    const buckets = useMemo(() => {
        const config = PERIODS.find((item) => item.key === period)!;
        const anchor = endDate && moment(endDate).isValid() && moment(endDate).isBefore(moment()) ? moment(endDate) : moment();
        // The hour view covers the whole day (12 AM to 11 PM) of the anchor date, the other views show the last 6 periods
        const length = period === 'hour' ? HOURS_IN_DAY : BUCKETS;
        const end = anchor.clone().endOf(period === 'hour' ? 'day' : (config.unit as any));
        const unit = config.unit === 'isoWeek' ? 'week' : config.unit;
        const list = Array.from({ length }, (_, index) => {
            const start = end.clone().subtract(length - 1 - index, unit as any).startOf(config.unit as any);
            return { start: start.valueOf(), stop: start.clone().add(1, unit as any).valueOf(), label: start.format(config.format), value: 0 };
        });
        seriesEntries(orders, metric).forEach((entry) => {
            const bucket = list.find((item) => entry.time >= item.start && entry.time < item.stop);
            if (bucket) bucket.value += entry.value;
        });
        return list;
    }, [orders, metric, period, endDate]);

    const formatValue = (value: number) => (metric === 'revenue' ? money(value) : String(Math.round(value)));
    const max = Math.max(1, ...buckets.map((bucket) => bucket.value));
    const ceiling = Math.max(4, Math.ceil(max / 4) * 4);
    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    const points = buckets.map((bucket, index) => ({ x: PAD.left + (plotW * index) / (buckets.length - 1), y: PAD.top + plotH - (bucket.value / ceiling) * plotH }));
    const bestIndex = buckets.reduce((best, bucket, index) => (bucket.value > buckets[best].value ? index : best), 0);
    const activeIndex = picked !== null && picked < buckets.length ? picked : bestIndex;
    const active = buckets[activeIndex];
    // The popup follows the hovered point (a click keeps it open for touch screens)
    const tipIndex = hovered ?? picked;
    const tipPoint = tipIndex !== null && points[tipIndex] ? points[tipIndex] : null;
    const tipX = tipPoint ? Math.min(Math.max(tipPoint.x - TIP_W / 2, 2), W - TIP_W - 2) : 0;
    const tipY = tipPoint ? (tipPoint.y - TIP_H - 12 < 2 ? tipPoint.y + 14 : tipPoint.y - TIP_H - 12) : 0;
    const activeMetric = METRICS.find((item) => item.key === metric)!;
    const headline = metric === 'revenue' ? data.net : metric === 'checkins' ? data.checkIn : data.sold;
    const percent = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 100) : 0);

    const tiles: { label: string; value: string; note?: string; negative?: boolean }[] = [
        { label: 'Gross Sales', value: money(data.gross), note: `${data.orderCount} orders` },
        { label: 'Sell-through', value: `${data.sellThrough}%`, note: `${data.sold}/${capacity || 0} tickets` },
        { label: 'Avg Order', value: money2(data.averageOrder), note: data.orderCount ? `${data.ticketsPerOrder.toFixed(1)} tickets each` : undefined },
        { label: 'Discounts', value: money(data.discounts), note: 'Promo and member', negative: true },
    ];

    return (
        <div className="mb-10 flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
                {tiles.map((item) => (
                    <div key={item.label} className={tile}>
                        <div className={`${tileLabel} mb-3`}>{item.label}</div>
                        <div className={`${tileValue} ${item.negative ? 'text-[#C62828] dark:text-[#FF5C6C]' : ''}`}>{item.value}</div>
                        {item.note && <div className={tileNote}>{item.note}</div>}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className={`${panel} ${tiers.length > 0 ? 'lg:col-span-3' : 'lg:col-span-5'}`}>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Chart metric">
                        {METRICS.map((item) => (
                            <button key={item.key} role="tab" aria-selected={metric === item.key} type="button" onClick={() => { setMetric(item.key); setPicked(null); }} className={`${chip} ${metric === item.key ? chipActive : chipIdle}`}>{item.label}</button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Chart period">
                        {PERIODS.map((item) => (
                            <button key={item.key} role="tab" aria-selected={period === item.key} type="button" onClick={() => { setPeriod(item.key); setPicked(null); }} className={`${chip} ${period === item.key ? chipActive : chipIdle}`}>{item.label}</button>
                        ))}
                    </div>
                </div>
                <div className="mb-1 text-[clamp(28px,3vw,36px)] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white">{metric === 'revenue' ? money(headline) : headline}</div>
                <div className="mb-3 text-sm text-grey40 dark:text-nightMuted">{active.label}: <span className="font-semibold text-grey10 dark:text-white">{formatValue(active.value)} {activeMetric.unit}</span></div>
                <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label={`${activeMetric.label} over time`}>
                    {[0, 1, 2, 3].map((step) => {
                        const y = PAD.top + (plotH * step) / 3;
                        return (
                            <g key={step}>
                                <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} className="stroke-black/[0.08] dark:stroke-white/10" strokeWidth={1} />
                                <text x={PAD.left - 8} y={y + 4} textAnchor="end" className="fill-grey50 dark:fill-nightMuted" fontSize={11}>{formatValue(ceiling - (ceiling * step) / 3)}</text>
                            </g>
                        );
                    })}
                    <path d={smoothPath(points)} fill="none" strokeWidth={2.5} strokeLinecap="round" className="stroke-primary dark:stroke-nightAccent" />
                    {points.map((point, index) => (
                        <circle key={buckets[index].start} cx={point.x} cy={point.y} r={index === activeIndex || index === hovered ? 5 : 12} className={index === activeIndex || index === hovered ? 'fill-primary stroke-white dark:fill-nightAccent dark:stroke-nightBg' : 'fill-transparent'} strokeWidth={2} style={{ cursor: 'pointer' }} onMouseEnter={() => { setHovered(index); setPicked(index); }} onMouseLeave={() => setHovered(null)} onClick={() => setPicked(index)} />
                    ))}
                    {tipIndex !== null && (
                        <g transform={`translate(${tipX}, ${tipY})`} pointerEvents="none">
                            <rect width={TIP_W} height={TIP_H} rx={10} className="fill-grey10 dark:fill-nightElevated" />
                            <text x={TIP_W / 2} y={16} textAnchor="middle" className="fill-white/70" fontSize={10}>{buckets[tipIndex].label}</text>
                            <text x={TIP_W / 2} y={33} textAnchor="middle" className="fill-white" fontSize={13} fontWeight={700}>{formatValue(buckets[tipIndex].value)} {activeMetric.unit}</text>
                        </g>
                    )}
                    {buckets.map((bucket, index) => (buckets.length > BUCKETS && index % 4 !== 0 ? null : (
                        <text key={bucket.start} x={points[index].x} y={H - 8} textAnchor="middle" className="fill-grey50 dark:fill-nightMuted" fontSize={11}>{bucket.label}</text>
                    )))}
                </svg>
            </div>
            {tiers.length > 0 && (
                <div className={`${panel} lg:col-span-2 lg:max-h-[420px] lg:overflow-y-auto`}>
                    <h3 className={heading}>Sales by Tier</h3>
                    <div className="flex flex-col gap-3">
                        {data.byTier.map((tier) => (
                            <div key={tier.name} className="rounded-[22px] bg-grey99 p-4 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <span className="flex min-w-0 items-center gap-2 font-semibold text-grey10 dark:text-white">
                                        {tier.color && <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: tier.color }} />}
                                        <span className="truncate">{tier.name}</span>
                                    </span>
                                    <span className="font-bold text-grey10 dark:text-white">{money(tier.revenue)}</span>
                                </div>
                                <div className="h-1.5 overflow-hidden rounded-full bg-grey95 dark:bg-white/10">
                                    <div className="h-full rounded-full bg-primary dark:bg-nightAccent" style={{ width: `${Math.min(100, percent(tier.sold, tier.capacity))}%`, ...(tier.color ? { backgroundColor: tier.color } : {}) }} />
                                </div>
                                <div className="mt-2 text-xs text-grey40 dark:text-nightMuted">{tier.sold}/{tier.capacity} seats sold ({percent(tier.sold, tier.capacity)}%) · {tier.checkIn} checked in</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className={panel}>
                    <h3 className={heading}>Payment Methods</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div className={tile}><div className={`${tileLabel} mb-3`}>Online</div><div className={tileValue}>{money(data.payments.card.amount)}</div><div className={tileNote}>{data.payments.card.count} orders</div></div>
                        <div className={tile}><div className={`${tileLabel} mb-3`}>Cash / Walk-in</div><div className={tileValue}>{money(data.payments.cash.amount)}</div><div className={tileNote}>{data.payments.cash.count} orders</div></div>
                    </div>
                </div>
                <div className={panel}>
                    <h3 className={heading}>Recent Orders</h3>
                    {data.recent.length === 0 && <p className="text-sm text-grey40 dark:text-nightMuted">No orders yet</p>}
                    <ul className="divide-y divide-black/[0.06] dark:divide-white/10">
                        {data.recent.map((order) => (
                            <li key={order.id} className="flex items-center justify-between gap-3 py-3">
                                <div className="min-w-0">
                                    <div className="truncate font-semibold text-grey10 dark:text-white">{order.name}</div>
                                    <div className="text-xs text-grey40 dark:text-nightMuted">{order.quantity} ticket{order.quantity === 1 ? '' : 's'} · {moment(order.at).fromNow()}</div>
                                </div>
                                <div className="font-bold text-grey10 dark:text-white">{money2(order.amount)}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
